import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ResultsShowcase = ({ results, theme = 'default' }) => {
  const getThemeClasses = () => {
    switch (theme) {
      case 'events':
        return {
          gradient: 'from-rose-50 to-pink-50',
          accent: 'text-rose-600',
          border: 'border-rose-200',
          bg: 'bg-rose-600'
        };
      case 'realestate':
        return {
          gradient: 'from-blue-50 to-slate-50',
          accent: 'text-blue-600',
          border: 'border-blue-200',
          bg: 'bg-blue-600'
        };
      case 'marketing':
        return {
          gradient: 'from-orange-50 to-red-50',
          accent: 'text-orange-600',
          border: 'border-orange-200',
          bg: 'bg-orange-600'
        };
      case 'design':
        return {
          gradient: 'from-emerald-50 to-teal-50',
          accent: 'text-emerald-600',
          border: 'border-emerald-200',
          bg: 'bg-emerald-600'
        };
      default:
        return {
          gradient: 'from-gray-50 to-gray-100',
          accent: 'text-primary',
          border: 'border-gray-200',
          bg: 'bg-primary'
        };
    }
  };

  const themeClasses = getThemeClasses();

  return (
    <div className={`bg-gradient-to-br ${themeClasses?.gradient} rounded-2xl p-8`}>
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Measurable Results
        </h3>
        <p className="text-gray-600">
          Data-driven outcomes that exceeded client expectations
        </p>
      </div>
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {results?.metrics?.map((metric, index) => (
          <div key={index} className="text-center">
            <div className={`w-16 h-16 ${themeClasses?.bg} rounded-full flex items-center justify-center mx-auto mb-3`}>
              <Icon name={metric?.icon} size={24} color="white" />
            </div>
            <div className={`text-3xl font-bold ${themeClasses?.accent} mb-1`}>
              {metric?.value}
            </div>
            <div className="text-sm text-gray-600">
              {metric?.label}
            </div>
            {metric?.change && (
              <div className="flex items-center justify-center gap-1 mt-1">
                <Icon 
                  name={metric?.change > 0 ? "TrendingUp" : "TrendingDown"} 
                  size={12} 
                  className={metric?.change > 0 ? "text-green-500" : "text-red-500"} 
                />
                <span className={`text-xs ${metric?.change > 0 ? "text-green-500" : "text-red-500"}`}>
                  {Math.abs(metric?.change)}%
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Before/After Comparison */}
      {results?.comparison && (
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            Before vs After
          </h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                  <Icon name="Minus" size={16} color="white" />
                </div>
                <h5 className="font-semibold text-gray-900">Before</h5>
              </div>
              <Image
                src={results?.comparison?.before?.image}
                alt="Before results"
                className="w-full h-32 object-cover rounded-lg mb-4"
              />
              <ul className="space-y-2">
                {results?.comparison?.before?.points?.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <Icon name="X" size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`bg-white rounded-xl p-6 border ${themeClasses?.border}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-8 h-8 ${themeClasses?.bg} rounded-full flex items-center justify-center`}>
                  <Icon name="Plus" size={16} color="white" />
                </div>
                <h5 className="font-semibold text-gray-900">After</h5>
              </div>
              <Image
                src={results?.comparison?.after?.image}
                alt="After results"
                className="w-full h-32 object-cover rounded-lg mb-4"
              />
              <ul className="space-y-2">
                {results?.comparison?.after?.points?.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <Icon name="Check" size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Client Feedback */}
      {results?.feedback && (
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-start gap-4">
            <Image
              src={results?.feedback?.avatar}
              alt={results?.feedback?.name}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex text-yellow-400">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={16} fill="currentColor" />
                  ))}
                </div>
                <span className="text-sm text-gray-500">5.0</span>
              </div>
              <p className="text-gray-700 italic mb-3">
                "{results?.feedback?.quote}"
              </p>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">
                  {results?.feedback?.name}
                </span>
                <span className="text-gray-500">•</span>
                <span className="text-gray-500 text-sm">
                  {results?.feedback?.title}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsShowcase;